import React from "react";
import classNames from "classnames";
import { Component } from "@7egend/web.core/lib/base";
import { memoizeOne } from "@7egend/web.core/lib/utils";
import { Range, getTrackBackground } from "react-range";
import styles from "./rangeSelector.module.css"

export interface RangeSelectorProps {
    /** range selector className */
    className?: string,
    /** range selector values */
    values: number[],
    /**
     * range selector step
     * @default 1
     */
    step?: number,
    /**
     * range selector min
     * @default 0
     */
    min: number,
    /**
     * range selector max
     * @default 100
     */
    max: number,
    /** range selector onChange event */
    onChange: (values: number[]) => void,
    /** range selector disabled */
    disabled?: boolean,
    /** range selector track */
    track: {
        /** element to set the track bar class name */
        className: string,
        /** set shadow class */
        shadowClass?: string,
        /** element to set the colors to the highlighted progress bar
         *
         * ex: ["#firstColor" : "#secondColor"]
         */
        colors?: string[],
    },
    /** range selector thumb */
    thumb: {
        /** set className to thumb */
        className: string,
    }
}

/**
 * # Range Selector
 * This class represents the input type range selector component
 *
 * # How to use
 *
 * ```tsx
 *  <RangeSelector
 *      values={values}
 *      min={min}
 *      max={max}
 *      step={step}
 *      onChange={onChange}
 *      className={**range selector classname**}
 *      track={{
 *           className: **your track bar classname**,
 *           colors: [color1, color2],
 *           shadowClass: **your shadow bar classname**,
 *       }}
 *       thumb={{
 *           className: **thumb classname**,
 *       }}>
 *   </RangeSelector>
 * ```
 *
 */
export class RangeSelector extends Component<RangeSelectorProps> {

    public getClasses = memoizeOne((className?: string) => {
        return classNames("_7g-rangeSelector", styles.rangeSelector, className);
    });

    public render() {
        const { values, min, max, step, disabled, onChange, className, track, thumb } = this.props;
        /** default items */
        const colorHighlight = !disabled ? "#999999" : "#000000";
        const colorDefault = "#E8E8E8";
        return (
            <div className={this.getClasses(className)}>
                <Range
                    values={values}
                    step={step}
                    min={min}
                    max={max}
                    onChange={onChange}
                    disabled={disabled}
                    renderTrack={({ props, children }) => (
                        <div
                            {...props}
                            style={{
                                background: getTrackBackground({
                                    values,
                                    colors: track.colors || [colorHighlight, colorDefault],
                                    min,
                                    max,
                                }),
                            }}
                            onMouseDown={props.onMouseDown}
                            onTouchStart={props.onTouchStart}
                            className={track.className}
                        >
                            {children}
                            {
                                track.shadowClass && track.colors &&
                                <div
                                    style={{
                                        width: `${values}%`,
                                    }}
                                    className={track.shadowClass} />
                            }
                        </div>
                    )}
                    renderThumb={({ props, isDragged }) => (
                        <div
                            {...props as any}
                            className={thumb.className}
                        />
                    )}>
                </Range>
            </div>
        );
    }
}
